// EIP-712 SubscriptionAgreement — single source of truth for typed-data
// shape. Consumed by both apps/web (signing) and apps/projector
// (verifying). Deterministic — same input → same digest → same recovered
// signer.
//
// Domain.verifyingContract is the Sale contract address; this is
// semantically meaningful (the contract that benefits from the
// subscription) even though the Sale contract does not currently
// verify on-chain.

import { z } from 'zod';
import {
  hashTypedData,
  recoverTypedDataAddress as viemRecoverTypedDataAddress,
  type Address,
  type Hex,
  type TypedDataDomain,
} from 'viem';

// The 9 fields cryptographically bound by the investor's signature.
// Field order MUST match the on-chain order so the digest is stable
// across implementations.
export const SUBSCRIPTION_AGREEMENT_TYPES = {
  SubscriptionAgreement: [
    { name: 'subscriptionId', type: 'string' },
    { name: 'investor', type: 'address' },
    { name: 'restaurantId', type: 'string' },
    { name: 'amountUsdc', type: 'uint256' },
    { name: 'tokens', type: 'uint256' },
    { name: 'pricePerToken', type: 'uint256' },
    { name: 'lockupMonths', type: 'uint256' },
    { name: 'termsHash', type: 'bytes32' },
    { name: 'nonce', type: 'uint256' },
  ],
} as const;

export const PRIMARY_TYPE = 'SubscriptionAgreement';
export const DOMAIN_NAME = 'RMINT Subscription';
export const DOMAIN_VERSION = '1';

// Zod schema for the inputs the caller provides. amountUsdc/tokens/etc.
// are bigints because uint256 values can exceed JS's safe integer range
// for token decimals. The hex types are tagged so callers must use the
// `0x...` prefix.
export const SubscriptionAgreementInputSchema = z.object({
  subscriptionId: z.string().min(1),
  investor: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'investor must be a 0x-prefixed 20-byte address'),
  restaurantId: z.string().min(1),
  amountUsdc: z.bigint().nonnegative(),
  tokens: z.bigint().nonnegative(),
  pricePerToken: z.bigint().nonnegative(),
  lockupMonths: z.bigint().nonnegative(),
  termsHash: z.string().regex(/^0x[a-fA-F0-9]{64}$/, 'termsHash must be a 0x-prefixed 32-byte hash'),
  nonce: z.bigint().nonnegative(),
  chainId: z.number().int().positive(),
  saleContractAddress: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'saleContractAddress must be a 0x-prefixed address'),
});

export type SubscriptionAgreementInput = z.infer<typeof SubscriptionAgreementInputSchema>;

export type SubscriptionAgreementMessage = {
  subscriptionId: string;
  investor: Address;
  restaurantId: string;
  amountUsdc: bigint;
  tokens: bigint;
  pricePerToken: bigint;
  lockupMonths: bigint;
  termsHash: Hex;
  nonce: bigint;
};

export type SubscriptionAgreementTypedData = {
  domain: TypedDataDomain;
  types: typeof SUBSCRIPTION_AGREEMENT_TYPES;
  primaryType: typeof PRIMARY_TYPE;
  message: SubscriptionAgreementMessage;
};

// Build the full EIP-712 typed-data object from the input. Consumers
// pass this directly to wallet.signTypedData (web) and
// recoverTypedDataAddress (server).
export function buildTypedData(input: SubscriptionAgreementInput): SubscriptionAgreementTypedData {
  const validated = SubscriptionAgreementInputSchema.parse(input);
  return {
    domain: {
      name: DOMAIN_NAME,
      version: DOMAIN_VERSION,
      chainId: validated.chainId,
      verifyingContract: validated.saleContractAddress as Address,
    },
    types: SUBSCRIPTION_AGREEMENT_TYPES,
    primaryType: PRIMARY_TYPE,
    message: {
      subscriptionId: validated.subscriptionId,
      investor: validated.investor as Address,
      restaurantId: validated.restaurantId,
      amountUsdc: validated.amountUsdc,
      tokens: validated.tokens,
      pricePerToken: validated.pricePerToken,
      lockupMonths: validated.lockupMonths,
      termsHash: validated.termsHash as Hex,
      nonce: validated.nonce,
    },
  };
}

// Compute the EIP-712 digest of the typed data (the hash that gets signed).
// Determinism guarantee: same input → same digest, byte-for-byte.
export function computeAgreementDigest(typedData: SubscriptionAgreementTypedData): Hex {
  return hashTypedData({
    domain: typedData.domain,
    types: typedData.types,
    primaryType: typedData.primaryType,
    message: typedData.message,
  });
}

// Recover the signer address from a signature over the typed data.
// Used by the projector to verify the signature matches the expected
// investor wallet before committing the event.
export async function recoverSignerAddress(
  typedData: SubscriptionAgreementTypedData,
  signature: Hex,
): Promise<Address> {
  return viemRecoverTypedDataAddress({
    domain: typedData.domain,
    types: typedData.types,
    primaryType: typedData.primaryType,
    message: typedData.message,
    signature,
  });
}
